import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  FolderIcon,
  ClockIcon,
  EyeIcon,
  MegaphoneIcon,
  BookOpenIcon,
  LightBulbIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, Avatar, InlineSpinner, EmptyState, Pagination } from '@/components/ui'
import { forumApi } from '@/api'
import type { ForumCategory, ThreadListItem } from '@/api/forum'

// Map icon names to Heroicons components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'megaphone': MegaphoneIcon,
  'message-circle': ChatBubbleLeftRightIcon,
  'book-open': BookOpenIcon,
  'lightbulb': LightBulbIcon,
  'help-circle': QuestionMarkCircleIcon,
  'users': UserGroupIcon,
}

const getCategoryIcon = (iconName: string | undefined) => {
  if (!iconName) return FolderIcon
  return iconMap[iconName] || FolderIcon
}

export default function ForumListPage() {
  const [categories, setCategories] = useState<ForumCategory[]>([])
  const [recentThreads, setRecentThreads] = useState<ThreadListItem[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  useEffect(() => {
    fetchData()
  }, [currentPage, searchQuery])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [categoriesRes, threadsRes] = await Promise.allSettled([
        forumApi.getCategories(),
        forumApi.getThreads({
          page: currentPage,
          search: searchQuery || undefined,
          ordering: '-created_at',
        }),
      ])

      if (categoriesRes.status === 'fulfilled') {
        setCategories(categoriesRes.value)
      }

      if (threadsRes.status === 'fulfilled') {
        setRecentThreads(threadsRes.value.results)
        setTotalPages(Math.ceil(threadsRes.value.count / 20))
        setTotalItems(threadsRes.value.count)
      }
    } catch (error) {
      console.error('Error fetching forum data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchData()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento forum..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Forum</h1>
          <p className="text-gray-600">Partecipa alle discussioni della community</p>
        </div>
        <Link
          to="/forum/new"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuova discussione
        </Link>
      </div>

      {/* Search */}
      <Card>
        <form onSubmit={handleSearch} className="flex gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca nelle discussioni..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cerca
          </button>
        </form>
      </Card>

      {/* Categories */}
      {categories.length > 0 && (
        <Card>
          <CardHeader title="Categorie" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/forum/category/${category.slug}`}
                className="flex items-start p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                {(() => {
                  const IconComponent = getCategoryIcon(category.icon)
                  return (
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center bg-primary-100"
                    >
                      <IconComponent className="h-6 w-6 text-primary-600" />
                    </div>
                  )
                })()}
                <div className="ml-4 flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {category.description || 'Nessuna descrizione'}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span>{category.threads_count} discussioni</span>
                    <span>{category.posts_count} messaggi</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Threads */}
      <Card>
        <CardHeader
          title={searchQuery ? `Risultati per "${searchQuery}"` : 'Discussioni recenti'}
        />
        {recentThreads.length > 0 ? (
          <>
            <div className="divide-y divide-gray-100">
              {recentThreads.map((thread) => (
                <Link
                  key={thread.id}
                  to={`/forum/thread/${thread.id}`}
                  className="flex items-start py-4 hover:bg-gray-50 transition-colors px-2 -mx-2 rounded-lg"
                >
                  <Avatar
                    size="md"
                    name={thread.author.display_name}
                    src={thread.author.avatar_url || undefined}
                  />
                  <div className="ml-4 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {thread.is_pinned && (
                        <Badge size="sm" variant="warning">Fissato</Badge>
                      )}
                      {thread.is_locked && (
                        <Badge size="sm" variant="danger">Chiuso</Badge>
                      )}
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {thread.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{thread.author.display_name}</span>
                      <Badge size="sm">{thread.forum_name}</Badge>
                      <span className="flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {formatDate(thread.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-gray-500 ml-4">
                    <div className="flex items-center">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      {thread.views_count}
                    </div>
                    <div className="flex items-center">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                      {thread.posts_count}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
            />
          </>
        ) : (
          <EmptyState
            icon={<ChatBubbleLeftRightIcon className="h-12 w-12" />}
            title={searchQuery ? 'Nessun risultato trovato' : 'Nessuna discussione'}
            description={
              searchQuery
                ? 'Prova a cercare con altri termini'
                : 'Sii il primo a iniziare una discussione!'
            }
          />
        )}
      </Card>
    </div>
  )
}
